import React from "react";
import { TabPane, ModalBody, ModalHeader } from "reactstrap";
import {
  ResponseActions,
  openLicenseModal,
  openOwnershipEvolutionModal,
} from "components/response";
import { FA } from "components/FA";
import { Loading } from "components/Utils";
import styles from "./DetailTab.module.css";
import { modalService } from "services";
import { useImagePreview } from "hooks";
import { ResponseType } from "data/Types";

const getLicenseInfo = (licensed = {}, licensable = {}) => {
  let status = licensed.status === "Y" ? "Yes" : "No";
  if (
    !licensed.status &&
    ((licensed.date && licensed.date !== "9999-12-31") ||
      (licensed.type && licensed.type !== "n/a") ||
      licensed.licensee ||
      licensed.licensedIn)
  ) {
    status = "Yes";
  }
  return (
    <>
      <dt>
        Potentially Licensed:{" "}
        <button
          className="btn btn-warning btn-sm"
          onClick={(e) => {
            e.preventDefault();
            openLicenseModal();
          }}
        >
          <FA icon="info" size="xs" /> Info
        </button>
      </dt>
      <dd>
        <em>Status:</em> {status}
        {licensed.date && !licensed.date.startsWith("9999-12-31") && (
          <>
            <br />
            <em>Date of license:</em>{" "}
            {new Date(licensed.date).toLocaleDateString()}
          </>
        )}
        <br />
        <em>Type of License:</em>{" "}
        {licensed.type && licensed.type !== "n/a" ? (
          licensed.type
        ) : (
          <abbr title="Not available">N/A</abbr>
        )}
        {licensed.licensee && (
          <>
            <br />
            <em>Licensee:</em> {licensed.licensee}
          </>
        )}
        {licensed.licensedIn && (
          <>
            <br />
            <em>Licensed in:</em> {licensed.licensedIn}
          </>
        )}
      </dd>
      <dt>
        Potentially Licensable:{" "}
        <button
          className="btn btn-warning btn-sm"
          onClick={(e) => {
            e.preventDefault();
            openLicenseModal();
          }}
        >
          <FA icon="info" size="xs" /> Info
        </button>
      </dt>
      <dd>
        {licensable.status === "Y" ? (
          "Yes"
        ) : licensable.status === "N" ? (
          "No"
        ) : (
          <abbr title="Not available">N/A</abbr>
        )}
        <br />
        {licensable.url &&
          Array.isArray(licensable.url) &&
          licensable.url.length > 0 &&
          licensable.url.map((url, i) => {
            let short = url;
            try {
              short = new URL(url).hostname;
            } catch (e) {
              console.error(url);
            }
            return (
              <React.Fragment key={`licenseable-url-${i}`}>
                {i > 0 && <br />}
                <a
                  title={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={url}
                >
                  <FA icon="globe" size="xs" /> {short}
                </a>
              </React.Fragment>
            );
          })}
      </dd>
    </>
  );
};

const Detail = ({ response }) => {
  const [image, isImage, isLoading] = useImagePreview(response);
  return (
    <dl>
      {image && (
        <dd>
          <img
            className={styles.image}
            src={image}
            alt={response.abstract || response.title}
          />
        </dd>
      )}
      {isLoading && (
        <dd className={styles.image}>
          {isImage ? <Loading /> : <FA icon="file-pdf" size="5x" />}
        </dd>
      )}
      {response.note && (
        <>
          <dt>Personal Note:</dt>
          <dd>{response.note}</dd>
          <hr />
        </>
      )}
      {response.patentNumber && (
        <>
          <dt>Patent Number:</dt>
          <dd>{response.patentNumber}</dd>
        </>
      )}
      {response.ipc && Array.isArray(response.ipc) && response.ipc.length > 0 && (
        <>
          <dt>
            <abbr title="International Patent Classification">IPC</abbr>:
          </dt>
          <dd>{response.ipc.join(", ")}</dd>
        </>
      )}
      {response.cpc && Array.isArray(response.cpc) && response.cpc.length > 0 && (
        <>
          <dt>
            <abbr title="Cooperative Patent Classification (based on IPC)">
              CPC
            </abbr>
            :
          </dt>
          <dd>{response.cpc.join(", ")}</dd>
        </>
      )}
      {response.date ? (
        <>
          <dt>Date published:</dt>
          <dd>{new Date(response.date).toLocaleDateString()}</dd>
        </>
      ) : (
        response.year && (
          <>
            <dt>Year:</dt>
            <dd>{response.year} </dd>
          </>
        )
      )}
      {response.dateGranted && (
        <>
          <dt>Date granted:</dt>
          <dd>{new Date(response.dateGranted).toLocaleDateString()}</dd>
        </>
      )}
      {response.authors && (
        <>
          <dt>Authors:</dt>
          <dd>{response.authors.map((p) => p.name).join(", ")}</dd>
        </>
      )}
      {response.owners && (
        <>
          <dt>
            Owners:{" "}
            <button
              className="btn btn-warning btn-sm"
              onClick={(e) => {
                e.preventDefault();
                openOwnershipEvolutionModal(response.owners);
              }}
            >
              <FA icon="info" size="xs" /> Info
            </button>
          </dt>
          <dd>{response.owners.map((p) => p.name).join(", ")}</dd>
        </>
      )}
      {response.abstract && (
        <>
          <dt>Abstract:</dt>
          <dd>{response.abstract}</dd>
        </>
      )}
      {response.language && (
        <>
          <dt>Language:</dt>
          <dd>{response.language}</dd>
        </>
      )}
      {response.publisher && (
        <>
          <dt>Publisher:</dt>
          <dd>{response.publisher}</dd>
        </>
      )}
      {response.fos && (
        <>
          <dt>
            <abbr title="Fields of Science and Technology">FOS</abbr>:
          </dt>
          <dd>{response.fos.join(", ")}</dd>
        </>
      )}
      {response.keywords && (
        <>
          <dt>Keywords:</dt>
          <dd>{response.keywords.join(", ")}</dd>
        </>
      )}
      {response.issue && (
        <>
          <dt>Issue:</dt>
          <dd>{response.issue}</dd>
        </>
      )}
      {response.doi && (
        <>
          <dt>
            <abbr title="Digital Object Identifier">DOI</abbr>:
          </dt>
          <dd>{response.doi}</dd>
        </>
      )}
      {response.venue && (
        <>
          <dt>Venue:</dt>
          <dd>{response.venue}</dd>
        </>
      )}
      {response.country && (
        <>
          <dt>Country:</dt>
          <dd>{response.country}</dd>
        </>
      )}
      {response.status && (
        <>
          <dt>Status:</dt>
          <dd>{response.status}</dd>
        </>
      )}
      {response.responseType === ResponseType.PATENT &&
        getLicenseInfo(
          response.potentiallyLicensed,
          response.potentiallyLicensable
        )}
      {response.claims > 0 && (
        <>
          <dt>Claims:</dt>
          <dd>{response.claims}</dd>
        </>
      )}
      {response.url && Array.isArray(response.url) && response.url.length > 0 && (
        <>
          <dt>
            <abbr title="Uniform Resource Locators">URLs</abbr>:
          </dt>
          <dd>
            {response.url.map((url, i) => {
              let short = url;
              try {
                short = new URL(url).hostname;
              } catch (e) {
                console.error(url);
              }
              return (
                <React.Fragment key={`${response.id}-detail-${i}`}>
                  {i > 0 && <br />}
                  <a
                    title={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={url}
                  >
                    <FA icon="globe" size="xs" /> {short}
                  </a>
                </React.Fragment>
              );
            })}
          </dd>
        </>
      )}
    </dl>
  );
};

const DetailTab = ({ response, tabId, className }) => {
  return (
    <TabPane tabId={tabId} className={className}>
      <Detail response={response} />
    </TabPane>
  );
};

const openDetailModal = (response) =>
  modalService.open({
    id: "detail",
    children: (
      <>
        <ModalHeader toggle={() => modalService.close("detail")}>
          {response.title}
        </ModalHeader>
        <ModalBody>
          <Detail response={response} />
          <ResponseActions response={response} detail />
        </ModalBody>
      </>
    ),
  });

export { DetailTab, openDetailModal };
